import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { fmtDate } from '../../lib/utils'

const STATUS_COLORS = {
  active: 'var(--green)',
  trialing: 'var(--blue)',
  comped: 'var(--orange)',
  archived: 'var(--muted)',
  inactive: 'var(--muted)',
  past_due: '#e6a817',
  cancelled: 'var(--red)',
  canceled: 'var(--red)',
  pending: 'var(--blue)',
  incomplete: 'var(--blue)',
}

const ADMIN_WORKSHOP_COLUMNS = `
  id, name, email, slug, created_at, subscription_status,
  stripe_customer_id, stripe_subscription_id, subscription_start,
  subscription_end, billing_amount_pence, billing_currency, billing_day,
  billing_checkout_url, billing_last_invoice_status,
  billing_last_invoice_amount_pence, billing_last_invoice_currency,
  billing_last_invoice_url, billing_last_payment_status,
  billing_next_payment_at, archived_at, archived_by, archive_reason,
  is_demo, demo_reset_at
`

const ADMIN_WORKSHOP_COLUMNS_WITH_MODULES = `${ADMIN_WORKSHOP_COLUMNS.trim()}, general_workshop_enabled`

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function moneyFromPence(pence, currency = 'gbp') {
  const amount = Number(pence || 0) / 100
  if (!amount) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: String(currency || 'gbp').toUpperCase(),
  }).format(amount)
}

function fmtDateTime(value) {
  if (!value) return '-'
  return new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function statusColor(status) {
  return STATUS_COLORS[status] || 'var(--muted)'
}

function trialExpired(workshop) {
  if (workshop.subscription_status !== 'trialing') return false
  if (!workshop.subscription_end) return true
  return new Date(workshop.subscription_end).getTime() <= Date.now()
}

function missingGeneralWorkshopColumn(error) {
  const text = [
    error?.code,
    error?.message,
    error?.details,
    error?.hint,
  ].filter(Boolean).join(' ').toLowerCase()
  return text.includes('general_workshop_enabled')
}

function hasGeneralWorkshopFlag(workshop) {
  return Object.prototype.hasOwnProperty.call(workshop || {}, 'general_workshop_enabled')
}

export default function AdminDashboard() {
  const [workshops, setWorkshops] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [overriding, setOverriding] = useState(null)
  const [showArchived, setShowArchived] = useState(false)
  const [lifecycleMsg, setLifecycleMsg] = useState(null)
  const [lifecycleError, setLifecycleError] = useState(null)
  const [billingOpen, setBillingOpen] = useState(null)
  const [billingForms, setBillingForms] = useState({})
  const [billingBusy, setBillingBusy] = useState(null)
  const [billingMsg, setBillingMsg] = useState(null)
  const [billingError, setBillingError] = useState(null)
  const [inviteForm, setInviteForm] = useState({
    workshopName: '',
    ownerEmail: '',
    ownerName: '',
    bookingSlug: '',
    status: 'pending'
  })
  const [inviting, setInviting] = useState(false)
  const [inviteMsg, setInviteMsg] = useState(null)
  const [inviteError, setInviteError] = useState(null)
  const [createdInvite, setCreatedInvite] = useState(null)

  const load = useCallback(async () => {
    let { data, error } = await supabase
      .from('workshops')
      .select(ADMIN_WORKSHOP_COLUMNS_WITH_MODULES)
      .order('created_at', { ascending: false })

    if (error && missingGeneralWorkshopColumn(error)) {
      const fallback = await supabase
        .from('workshops')
        .select(ADMIN_WORKSHOP_COLUMNS)
        .order('created_at', { ascending: false })
      data = fallback.data
      error = fallback.error
    }

    if (error) {
      console.error('Could not load workshops:', error.message || error)
      setWorkshops([])
      setLoading(false)
      return
    }

    setWorkshops(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  async function runWorkshopAction(workshop, action, options = {}) {
    setOverriding(`${action}:${workshop.id}`)
    setLifecycleMsg(null)
    setLifecycleError(null)

    try {
      const token = await adminToken()
      const res = await fetch('/.netlify/functions/admin-workshops', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action,
          workshopId: workshop.id,
          ...options,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Workshop action failed.')

      setLifecycleMsg(action === 'delete_empty'
        ? `${workshop.name} was deleted.`
        : action === 'reset_demo'
          ? `${workshop.name} demo data reset.${data.storage_cleanup?.warnings?.length ? ' Some storage files need manual cleanup.' : ''}`
          : `${workshop.name} updated.`)
      await load()
      return data
    } catch (err) {
      setLifecycleError(err.message || 'Workshop action failed.')
      return null
    } finally {
      setOverriding(null)
    }
  }

  function setInviteField(field, value) {
    setInviteForm(prev => ({ ...prev, [field]: value }))
  }

  function billingDraft(workshop) {
    return {
      amount: ((Number(workshop.billing_amount_pence) || 9999) / 100).toFixed(2),
      day: String(workshop.billing_day || 1),
      description: 'Rebound software subscription',
      ...(billingForms[workshop.id] || {}),
    }
  }

  function setBillingField(workshopId, field, value) {
    setBillingForms(prev => ({
      ...prev,
      [workshopId]: {
        ...(prev[workshopId] || {}),
        [field]: value,
      },
    }))
  }

  async function adminToken() {
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) throw new Error('Please sign in again before changing billing.')
    return token
  }

  async function runBillingAction(workshop, action, options = {}) {
    const draft = billingDraft(workshop)
    const amountPence = Math.round(Number(draft.amount || 0) * 100)
    const billingDay = Number(draft.day || 1)
    setBillingBusy(`${action}:${workshop.id}`)
    setBillingMsg(null)
    setBillingError(null)

    try {
      const token = await adminToken()
      const res = await fetch('/.netlify/functions/admin-billing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action,
          workshopId: workshop.id,
          amountPence,
          billingDay,
          currency: 'gbp',
          description: draft.description,
          ...options,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Billing action failed.')

      if (action === 'create_checkout') {
        setBillingMsg(data.email_sent
          ? `Direct debit setup link created and emailed to ${workshop.email}.`
          : 'Direct debit setup link created.')
      } else if (action === 'issue_invoice') {
        setBillingMsg('Invoice issued in Stripe.')
      } else if (action === 'cancel_subscription') {
        setBillingMsg('Stripe subscription cancelled.')
      } else {
        setBillingMsg('Billing synced from Stripe.')
      }

      await load()
      return data
    } catch (err) {
      setBillingError(err.message || 'Billing action failed.')
      return null
    } finally {
      setBillingBusy(null)
    }
  }

  async function copyText(text, message) {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setBillingMsg(message)
      setBillingError(null)
    } catch {
      setBillingError('Could not copy automatically. Select the link and copy it manually.')
    }
  }

  async function inviteWorkshop(e) {
    e.preventDefault()
    setInviteMsg(null)
    setInviteError(null)
    setCreatedInvite(null)
    setInviting(true)

    try {
      const token = await adminToken()
      const res = await fetch('/.netlify/functions/invite-workshop', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(inviteForm)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Workshop invite failed.')

      setInviteMsg(`Invite sent to ${inviteForm.ownerEmail}. Booking slug: ${data.workshop.slug}`)
      setCreatedInvite(data.workshop)
      setInviteForm({
        workshopName: '',
        ownerEmail: '',
        ownerName: '',
        bookingSlug: '',
        status: 'pending'
      })
      await load()
    } catch (err) {
      setInviteError(err.message || 'Workshop invite failed.')
      setCreatedInvite(null)
    } finally {
      setInviting(false)
    }
  }

  const q = search.trim().toLowerCase()
  const visibleWorkshops = showArchived ? workshops : workshops.filter(w => !w.archived_at)
  const filtered = visibleWorkshops.filter(w =>
    !q || w.name?.toLowerCase().includes(q) || w.email?.toLowerCase().includes(q) || w.slug?.toLowerCase().includes(q)
  )

  const hasAccess = (w) => {
    if (w.archived_at) return false
    if (w.subscription_status === 'active' || w.subscription_status === 'comped') return true
    if (w.subscription_status === 'trialing') return !trialExpired(w)
    return false
  }
  const paidStatuses = new Set(['active', 'trialing'])
  const activeCount = workshops.filter(hasAccess).length
  const archivedCount = workshops.filter(w => w.archived_at).length
  const demoCount = workshops.filter(w => w.is_demo).length
  const mrrPence = workshops
    .filter(w => !w.archived_at && paidStatuses.has(w.subscription_status) && !trialExpired(w))
    .reduce((sum, w) => sum + (Number(w.billing_amount_pence) || 9999), 0)
  const suggestedSlug = inviteForm.bookingSlug.trim() || slugify(inviteForm.workshopName)
  const bookingPreviewUrl = suggestedSlug && typeof window !== 'undefined'
    ? `${window.location.origin}/book/${suggestedSlug}`
    : ''
  const createdBookingUrl = createdInvite?.slug && typeof window !== 'undefined'
    ? `${window.location.origin}/book/${createdInvite.slug}`
    : ''

  if (loading) return <div className="empty"><span className="spinner" style={{ width: 20, height: 20 }} /></div>

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>All Workshops</div>
          <div className="text-muted fs12 mt4">Manage subscriptions, direct debits, invoices, and access</div>
        </div>
      </div>

      <div className="grid-4" style={{ marginBottom: 20 }}>
        <div className="stat"><span className="stat-val">{workshops.length}</span><span className="stat-label">Total Workshops</span></div>
        <div className="stat"><span className="stat-val text-green">{activeCount}</span><span className="stat-label">With Access</span></div>
        <div className="stat"><span className="stat-val">{archivedCount}</span><span className="stat-label">Archived</span></div>
        <div className="stat"><span className="stat-val text-green">{moneyFromPence(mrrPence)}</span><span className="stat-label">MRR</span></div>
      </div>

      <form className="card" onSubmit={inviteWorkshop} style={{ padding: 18, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Invite Workshop</div>
            <div className="text-muted fs12 mt4">Create the workshop, reserve its booking slug, and email the owner their setup invite.</div>
          </div>
          <button className="btn btn-green" type="submit" disabled={inviting}>
            {inviting ? 'Sending...' : 'Send Workshop Invite'}
          </button>
        </div>

        {inviteMsg && (
          <div className="alert alert-success" style={{ marginBottom: 14 }}>
            {inviteMsg}
            {createdBookingUrl && (
              <div className="fs12" style={{ marginTop: 8 }}>
                Booking page: <a href={createdBookingUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)' }}>{createdBookingUrl}</a>
              </div>
            )}
          </div>
        )}
        {inviteError && (
          <div className="alert alert-error" style={{ marginBottom: 14 }}>
            {inviteError}
          </div>
        )}

        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <div className="field">
            <label>Workshop Name</label>
            <input
              value={inviteForm.workshopName}
              onChange={e => setInviteField('workshopName', e.target.value)}
              required
              placeholder="Torque Collective"
            />
          </div>
          <div className="field">
            <label>Owner Email</label>
            <input
              type="email"
              value={inviteForm.ownerEmail}
              onChange={e => setInviteField('ownerEmail', e.target.value)}
              required
              placeholder="owner@example.com"
            />
          </div>
          <div className="field">
            <label>Owner Name</label>
            <input
              value={inviteForm.ownerName}
              onChange={e => setInviteField('ownerName', e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="field">
            <label>Booking Slug</label>
            <input
              value={inviteForm.bookingSlug}
              onChange={e => setInviteField('bookingSlug', slugify(e.target.value))}
              placeholder="Auto from workshop name"
            />
          </div>
          <div className="field">
            <label>Starting Status</label>
            <select value={inviteForm.status} onChange={e => setInviteField('status', e.target.value)}>
              <option value="pending">Pending subscription</option>
              <option value="active">Active paid</option>
              <option value="trialing">Trialing - 3 months</option>
              <option value="comped">Comped / internal</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="alert alert-info" style={{ marginTop: 2, marginBottom: 0 }}>
          <div className="fw7" style={{ marginBottom: 6 }}>Owner handover</div>
          <div className="fs12" style={{ marginBottom: 8 }}>
            After accepting the invite, the owner will land on the dashboard and complete the launch checklist: workshop details, service menu, booking slug, and daily capacity.
          </div>
          <div className="fs12">
            Booking URL preview: {bookingPreviewUrl ? <span className="text-white">{bookingPreviewUrl}</span> : <span>generated from the workshop name</span>}
          </div>
        </div>
      </form>

      {lifecycleMsg && <div className="alert alert-success">{lifecycleMsg}</div>}
      {lifecycleError && <div className="alert alert-error">{lifecycleError}</div>}
      {billingMsg && <div className="alert alert-success">{billingMsg}</div>}
      {billingError && <div className="alert alert-error">{billingError}</div>}

      <div className="search-wrap" style={{ marginBottom: 16 }}>
        <span className="search-icon">Search</span>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by workshop name or email..." />
        {search && <button type="button" className="search-clear" onClick={() => setSearch('')}>x</button>}
      </div>

      <div className="action-row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="text-muted fs12">
          Showing {filtered.length} workshop{filtered.length === 1 ? '' : 's'}{showArchived ? ' including archived' : ''}. Demo accounts: {demoCount}.
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowArchived(v => !v)}>
          {showArchived ? 'Hide Archived' : `Show Archived (${archivedCount})`}
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table>
          <thead>
            <tr>
              <th>Workshop</th>
              <th>Status</th>
              <th>Billing</th>
              <th>Next payment</th>
              <th>Last invoice</th>
              <th>Stripe</th>
              <th>Access</th>
              <th>Admin</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(w => {
              const draft = billingDraft(w)
              const stripeCustomerUrl = w.stripe_customer_id ? `https://dashboard.stripe.com/customers/${w.stripe_customer_id}` : ''
              const busy = billingBusy?.endsWith(`:${w.id}`)
              const lifecycleBusy = overriding?.endsWith(`:${w.id}`)
              const expired = trialExpired(w)
              const archived = Boolean(w.archived_at)
              const bookingUrl = w.slug && typeof window !== 'undefined' ? `${window.location.origin}/book/${w.slug}` : ''
              const generalFlagKnown = hasGeneralWorkshopFlag(w)
              const generalWorkshopEnabled = w.general_workshop_enabled === true
              return (
                <React.Fragment key={w.id}>
                  <tr style={{ opacity: archived ? 0.62 : 1 }}>
                    <td>
                      <div className="text-white fw7">{w.name}</div>
                      <div className="text-muted fs12">{w.email}</div>
                      <div className="fs11 text-muted">{w.slug ? `/book/${w.slug}` : 'No booking slug'}</div>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 5 }}>
                        {w.is_demo && <span className="job-status-chip job-status-blue">Demo</span>}
                        {generalWorkshopEnabled && <span className="job-status-chip job-status-green">General Module</span>}
                        {archived && <span className="job-status-chip job-status-muted">Archived</span>}
                      </div>
                      {w.is_demo && w.demo_reset_at && <div className="fs11 text-muted mt4">Reset {fmtDate(w.demo_reset_at)}</div>}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 3,
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em',
                        background: statusColor(archived ? 'archived' : w.subscription_status) + '22',
                        color: statusColor(archived ? 'archived' : w.subscription_status)
                      }}>
                        {w.subscription_status || 'inactive'}
                      </span>
                      {w.subscription_status === 'trialing' ? (
                        <div className={`fs11 mt4 ${expired ? 'text-orange' : 'text-muted'}`}>
                          Trial ends {fmtDate(w.subscription_end)}{expired ? ' - expired' : ''}
                        </div>
                      ) : (
                        <div className="fs11 text-muted mt4">Renews {fmtDate(w.subscription_end)}</div>
                      )}
                      {archived && <div className="fs11 text-muted mt4">Archived {fmtDate(w.archived_at)}{w.archive_reason ? ` - ${w.archive_reason}` : ''}</div>}
                    </td>
                    <td>
                      <div className="text-white fw7">{moneyFromPence(w.billing_amount_pence || 9999, w.billing_currency || 'gbp')} / month</div>
                      <div className="fs11 text-muted">Collection day {w.billing_day || 1}</div>
                    </td>
                    <td className="fs12 text-muted">{fmtDateTime(w.billing_next_payment_at)}</td>
                    <td>
                      <div className="fs12 text-white">{w.billing_last_invoice_status || '-'}</div>
                      <div className="fs11 text-muted">
                        {w.billing_last_invoice_amount_pence ? moneyFromPence(w.billing_last_invoice_amount_pence, w.billing_last_invoice_currency || 'gbp') : '-'}
                      </div>
                      {w.billing_last_invoice_url && (
                        <a href={w.billing_last_invoice_url} target="_blank" rel="noopener noreferrer" className="fs11 text-orange">Open invoice</a>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBillingOpen(open => open === w.id ? null : w.id)}>
                          {billingOpen === w.id ? 'Close' : 'Billing'}
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => runBillingAction(w, 'sync')} disabled={busy}>
                          Sync
                        </button>
                        {stripeCustomerUrl && (
                          <a className="btn btn-ghost btn-sm" href={stripeCustomerUrl} target="_blank" rel="noopener noreferrer">Stripe</a>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" className="btn btn-green btn-sm" onClick={() => runWorkshopAction(w, 'start_trial')} disabled={lifecycleBusy || archived}>
                          3mo Trial
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            const months = window.prompt('Extend trial by how many months?', '1')
                            if (months) runWorkshopAction(w, 'extend_trial', { months })
                          }}
                          disabled={lifecycleBusy || archived}
                        >
                          Extend
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => runWorkshopAction(w, 'mark_active')} disabled={lifecycleBusy || archived}>
                          Active
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => runWorkshopAction(w, 'mark_comped')} disabled={lifecycleBusy || archived}>
                          Comp
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => runWorkshopAction(w, 'mark_inactive')} disabled={lifecycleBusy}>
                          Inactive
                        </button>
                        <button type="button" className="btn btn-red btn-sm" onClick={() => runWorkshopAction(w, 'mark_cancelled')} disabled={lifecycleBusy}>
                          Cancel
                        </button>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {bookingUrl && <a className="btn btn-ghost btn-sm" href={bookingUrl} target="_blank" rel="noopener noreferrer">Booking</a>}
                        {generalFlagKnown ? (
                          <button
                            type="button"
                            className={`btn ${generalWorkshopEnabled ? 'btn-green' : 'btn-ghost'} btn-sm`}
                            onClick={() => runWorkshopAction(w, generalWorkshopEnabled ? 'disable_general_workshop' : 'enable_general_workshop')}
                            disabled={lifecycleBusy || archived}
                            title="Superadmin-only gate for the dormant General Workshop Module foundation."
                          >
                            {generalWorkshopEnabled ? 'Disable General' : 'Enable General'}
                          </button>
                        ) : (
                          <span className="job-status-chip job-status-muted" title="Apply schema_v75.sql before using this control.">General pending</span>
                        )}
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => runWorkshopAction(w, w.is_demo ? 'unmark_demo' : 'mark_demo')} disabled={lifecycleBusy}>
                          {w.is_demo ? 'Undemo' : 'Demo'}
                        </button>
                        {w.is_demo && (
                          <button
                            type="button"
                            className="btn btn-red btn-sm"
                            onClick={() => {
                              const confirmation = window.prompt(`Reset demo data for ${w.name}? This deletes demo customers, jobs, reports, setup data, imports, notifications, and audit rows. Type RESET DEMO to continue:`)
                              if (confirmation !== null) runWorkshopAction(w, 'reset_demo', { confirmation })
                            }}
                            disabled={lifecycleBusy}
                            title="Deletes operational data for demo workshops only. Preserves workshop setup, team access, services, and demo status."
                          >
                            Reset Demo
                          </button>
                        )}
                        {archived ? (
                          <button type="button" className="btn btn-green btn-sm" onClick={() => runWorkshopAction(w, 'restore')} disabled={lifecycleBusy}>
                            Restore
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                              const reason = window.prompt(`Archive ${w.name}? Add a short reason:`, 'No longer active')
                              if (reason !== null) runWorkshopAction(w, 'archive', { reason })
                            }}
                            disabled={lifecycleBusy}
                          >
                            Archive
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-red btn-sm"
                          onClick={() => {
                            if (window.confirm(`Delete ${w.name} only if it is completely empty/test-only? This cannot be undone.`)) {
                              runWorkshopAction(w, 'delete_empty')
                            }
                          }}
                          disabled={lifecycleBusy}
                          title="Only succeeds for genuinely empty test workshops. Archive real workshops instead."
                        >
                          Delete Empty
                        </button>
                      </div>
                    </td>
                  </tr>

                  {billingOpen === w.id && (
                    <tr>
                      <td colSpan="8" style={{ background: 'var(--black)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12, marginBottom: 12 }}>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label>Monthly Amount</label>
                            <input
                              type="number"
                              min="1"
                              step="0.01"
                              value={draft.amount}
                              onChange={e => setBillingField(w.id, 'amount', e.target.value)}
                            />
                          </div>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label>Payment Day</label>
                            <input
                              type="number"
                              min="1"
                              max="31"
                              value={draft.day}
                              onChange={e => setBillingField(w.id, 'day', e.target.value)}
                            />
                          </div>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label>Invoice Description</label>
                            <input
                              value={draft.description}
                              onChange={e => setBillingField(w.id, 'description', e.target.value)}
                            />
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => runBillingAction(w, 'create_checkout', { sendEmail: true })} disabled={busy}>
                            Create + Email DD Link
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => runBillingAction(w, 'create_checkout')} disabled={busy}>
                            Create DD Link
                          </button>
                          <button
                            type="button"
                            className="btn btn-green btn-sm"
                            onClick={() => {
                              if (window.confirm(`Issue an invoice to ${w.name} for ${draft.amount} GBP now?`)) {
                                runBillingAction(w, 'issue_invoice')
                              }
                            }}
                            disabled={busy}
                          >
                            Issue Invoice Now
                          </button>
                          {w.stripe_subscription_id && (
                            <button
                              type="button"
                              className="btn btn-red btn-sm"
                              onClick={() => {
                                if (window.confirm(`Cancel the Stripe subscription for ${w.name}?`)) {
                                  runBillingAction(w, 'cancel_subscription')
                                }
                              }}
                              disabled={busy}
                            >
                              Cancel Stripe Subscription
                            </button>
                          )}
                        </div>

                        {w.billing_checkout_url && (
                          <div style={{ marginTop: 12 }}>
                            <label>Direct Debit Setup Link</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <input readOnly value={w.billing_checkout_url} />
                              <button type="button" className="btn btn-ghost btn-sm" onClick={() => copyText(w.billing_checkout_url, 'Direct debit link copied.')}>Copy</button>
                              <a className="btn btn-ghost btn-sm" href={w.billing_checkout_url} target="_blank" rel="noopener noreferrer">Open</a>
                            </div>
                          </div>
                        )}

                        <div className="fs11 text-muted" style={{ marginTop: 10 }}>
                          Stripe handles the direct debit mandate, invoices, payment retries, and bank details. Rebound stores only Stripe IDs, status, invoice links, and the chosen collection day.
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
